import { Link } from "@tanstack/react-router";
import { Copy01, Download01, RefreshCw01 } from "@untitledui/icons";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/base/buttons/button";
import {
  type CapturedError,
  collectApp,
  collectEnv,
  collectFeatures,
  collectStorage,
  type DiagnosticsReport,
  makeRef,
  type ProbeResult,
  probeSpecs,
  reportToText,
  runProbes,
} from "@/lib/diagnostics";
import { cx } from "@/utils/cx";

const statusClass: Record<ProbeResult["status"], string> = {
  ok: "text-success-primary",
  warn: "text-warning-primary",
  fail: "text-error-primary",
};

const statusLabel: Record<ProbeResult["status"], string> = { ok: "OK", warn: "Check", fail: "Failed" };

export function AboutPage() {
  const [report, setReport] = useState<DiagnosticsReport | null>(null);
  const [probes, setProbes] = useState<Record<string, ProbeResult>>({});
  const [running, setRunning] = useState(false);
  const [toast, setToast] = useState("");
  const errors = useRef<CapturedError[]>([]);

  useEffect(() => {
    const onError = (e: ErrorEvent) => errors.current.push({ type: "error", message: String(e.message) });
    const onRejection = (e: PromiseRejectionEvent) =>
      errors.current.push({ type: "unhandledrejection", message: String(e.reason?.message ?? e.reason) });
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  const run = useCallback(async () => {
    setRunning(true);
    setProbes({});
    errors.current = [];
    const startedAt = new Date().toISOString();
    const ref = makeRef();
    const draft: DiagnosticsReport = {
      version: 1,
      ref,
      startedAt,
      finishedAt: startedAt,
      app: collectApp(),
      env: collectEnv(),
      storage: {},
      features: collectFeatures(),
      probes: [],
      errors: [],
    };
    setReport(draft);
    const [storage, results] = await Promise.all([
      collectStorage(),
      runProbes((r) => setProbes((prev) => ({ ...prev, [r.id]: r }))),
    ]);
    setReport({
      ...draft,
      storage,
      probes: results,
      errors: [...errors.current],
      finishedAt: new Date().toISOString(),
    });
    setRunning(false);
  }, []);

  useEffect(() => {
    run();
  }, [run]);

  const flash = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2500);
  };

  const copy = async () => {
    if (!report) return;
    try {
      await navigator.clipboard.writeText(reportToText(report));
      flash("Copied");
    } catch {
      flash("Copy failed. Use Download instead.");
    }
  };

  const download = () => {
    if (!report) return;
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `unbox-diagnostics-${report.ref}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const skew = Object.values(probes).find((p) => p.clockSkewSec !== undefined && Math.abs(p.clockSkewSec) > 60);

  return (
    <div className="min-h-screen bg-primary text-primary">
      <div className="mx-auto flex max-w-3xl flex-col gap-10 px-6 py-12">
        <header className="flex flex-col gap-4">
          <Link to="/" className="w-40">
            <img src="/logo.png" alt="Unbox" />
          </Link>
          <div className="flex flex-col gap-1">
            <h1 className="text-display-xs font-semibold">About Unbox</h1>
            <p className="text-sm text-tertiary">
              This page checks whether your browser and network can reach Unbox and its sign-in service. It takes about
              ten seconds. When it finishes, send us the reference code or copy the report into your support request.
              The report contains browser, screen and network details. It never contains passwords, cookie values or
              page content.
            </p>
          </div>
        </header>

        <section className="flex flex-wrap items-center gap-6 rounded-xl border border-secondary bg-secondary p-6">
          <div className="flex flex-col gap-1">
            <span className="text-xs uppercase tracking-wide text-tertiary">Reference code</span>
            <code className="font-mono text-display-sm font-bold tracking-[0.2em]">{report?.ref ?? "------"}</code>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button color="primary" size="md" iconLeading={Copy01} onClick={copy} isDisabled={!report || running}>
              Copy report
            </Button>
            <Button
              color="secondary"
              size="md"
              iconLeading={Download01}
              onClick={download}
              isDisabled={!report || running}
            >
              Download report
            </Button>
            <Button color="tertiary" size="md" iconLeading={RefreshCw01} onClick={run} isLoading={running}>
              Run again
            </Button>
            {toast && <span className="text-sm font-medium text-success-primary">{toast}</span>}
          </div>
        </section>

        <Section title="Connection checks">
          <table className="w-full text-sm">
            <tbody>
              {probeSpecs().map((spec) => {
                const p = probes[spec.id];
                return (
                  <tr key={spec.id} className="border-b border-secondary align-top last:border-0">
                    <th className="w-1/3 py-3 pr-4 text-left font-medium text-secondary">{spec.name}</th>
                    <td className="py-3">
                      {p ? (
                        <div className="flex flex-col gap-0.5">
                          <span>
                            <span className={cx("font-semibold", statusClass[p.status])}>{statusLabel[p.status]}</span>{" "}
                            <span className="text-secondary">
                              {p.summary}
                              {p.httpStatus !== undefined && ` (HTTP ${p.httpStatus})`} · {p.elapsedMs}ms
                            </span>
                          </span>
                          {p.detail && <span className="break-all text-xs text-tertiary">{p.detail}</span>}
                        </div>
                      ) : (
                        <span className="text-tertiary">Testing…</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {skew && (
            <p className="mt-3 text-sm text-warning-primary">
              This device's clock is {skew.clockSkewSec}s off server time. Sign-in can fail when the clock is more than
              a few minutes out.
            </p>
          )}
        </Section>

        {report && (
          <>
            <KeyValueSection title="App" data={report.app} />
            <KeyValueSection title="Browser and device" data={report.env} />
            <KeyValueSection
              title="Storage and cookies"
              data={report.storage}
              highlight={(v) => (String(v).startsWith("BLOCKED") ? "fail" : String(v) === "ok" ? "ok" : undefined)}
            />
            <KeyValueSection
              title="Browser features"
              data={Object.fromEntries(Object.entries(report.features).map(([k, v]) => [k, v ? "yes" : "no"]))}
              highlight={(v) => (v === "yes" ? "ok" : "fail")}
            />
            <Section title="Errors captured on this page">
              {report.errors.length === 0 ? (
                <p className="text-sm text-success-primary">No errors captured</p>
              ) : (
                <ul className="flex flex-col gap-1 text-sm text-error-primary">
                  {report.errors.map((e, i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: errors are append-only for the life of a run
                    <li key={i}>
                      {e.type}: {e.message}
                    </li>
                  ))}
                </ul>
              )}
            </Section>
          </>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-3">
      <h2 className="border-b border-primary pb-2 text-sm font-semibold uppercase tracking-wide text-secondary">
        {title}
      </h2>
      {children}
    </section>
  );
}

function KeyValueSection({
  title,
  data,
  highlight,
}: {
  title: string;
  data: Record<string, string | number | boolean>;
  highlight?: (value: string | number | boolean) => ProbeResult["status"] | undefined;
}) {
  return (
    <Section title={title}>
      <table className="w-full font-mono text-xs">
        <tbody>
          {Object.entries(data).map(([k, v]) => {
            const status = highlight?.(v);
            return (
              <tr key={k} className="border-b border-secondary align-top last:border-0">
                <th className="w-1/3 py-1.5 pr-4 text-left font-normal text-tertiary">{k}</th>
                <td className={cx("break-all py-1.5", status && statusClass[status], status && "font-semibold")}>
                  {String(v)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Section>
  );
}
